/* Notification Center → Email Templates → Insert Field (RTCFR 2026-10-19).
 *
 * The Insert Field list is DATABASE-DRIVEN (dbo.AI_NotificationTemplateField via
 * NotificationTemplateFieldsController): one row per (Rule Category, field), so the
 * editor shows only the active fields of the template's category and an administrator
 * adds / edits / retires fields at runtime (Manage Fields) without a code change. */
import { api } from '../../api/client';

const BASE = '/api/v1/notification-template-fields';

/** How a field gets its value. */
export type TemplateFieldResolver = 'Database' | 'Alert';
/** How a Database value is formatted. */
export type TemplateFieldDataType = 'Text' | 'Number' | 'Date' | 'DateTime' | 'Boolean';

/** One Insert Field. */
export interface TemplateField {
  id: string;
  categoryCode: number;
  categoryName: string;
  fieldName: string;
  /** The placeholder, e.g. "{{SampleId}}". */
  fieldCode: string;
  resolverType: TemplateFieldResolver;
  sourceTable?: string | null;
  sourceColumn?: string | null;
  dataType: TemplateFieldDataType;
  format?: string | null;
  displayOrder: number;
  isActive: boolean;
}

/** The New / Edit field form. The server validates every value (authoritative). */
export interface TemplateFieldInput {
  categoryCode: number;
  fieldName: string;
  fieldCode: string;
  resolverType: TemplateFieldResolver;
  sourceTable?: string | null;
  sourceColumn?: string | null;
  dataType: TemplateFieldDataType;
  format?: string | null;
  /** null = after the category's last field. */
  displayOrder?: number | null;
  isActive: boolean;
}

/** A value an alert computes itself — what an Alert field may name. */
export interface AlertFieldOption { code: string; name: string; dataType: TemplateFieldDataType; sourceTable?: string | null; sourceColumn?: string | null }
export interface TemplateFieldOptions { resolverTypes: TemplateFieldResolver[]; dataTypes: TemplateFieldDataType[]; alertFields: AlertFieldOption[] }
/** An approved Source Table. */
export interface SourceTable { table: string; label: string; path: string }
/** A column of an approved Source Table. */
export interface SourceColumn { column: string; sqlType: string; suggestedDataType: TemplateFieldDataType }

/** A placeholder in a template that needs attention. */
export interface TemplateFieldWarning { fieldCode: string; kind: 'Inactive' | 'NotConfigured' | 'Unresolved'; message: string }
/** The editor's Preview, rendered by the server with one real job's values. */
export interface TemplatePreview { subject: string; htmlBody: string; jobId?: string | null; sampleId?: string | null; warnings: TemplateFieldWarning[] }

const data = <T,>(res: { data: unknown }) => (res.data as { data: T }).data;

/** The fields of a category (active only unless `includeInactive`), in Display Order; every category when omitted. */
export async function listTemplateFields(categoryCode?: number, includeInactive = false): Promise<TemplateField[]> {
  return data<TemplateField[]>(await api.get(BASE, { params: { categoryCode, includeInactive } }));
}
export async function getTemplateFieldOptions(): Promise<TemplateFieldOptions> {
  return data<TemplateFieldOptions>(await api.get(`${BASE}/options`));
}
export async function listSourceTables(): Promise<SourceTable[]> {
  return data<SourceTable[]>(await api.get(`${BASE}/sources`));
}
export async function listSourceColumns(table: string): Promise<SourceColumn[]> {
  return data<SourceColumn[]>(await api.get(`${BASE}/sources/${encodeURIComponent(table)}/columns`));
}
export async function createTemplateField(input: TemplateFieldInput): Promise<TemplateField> {
  return data<TemplateField>(await api.post(BASE, input));
}
export async function updateTemplateField(id: string, input: TemplateFieldInput): Promise<TemplateField> {
  return data<TemplateField>(await api.put(`${BASE}/${id}`, input));
}
export async function deleteTemplateField(id: string): Promise<void> {
  await api.delete(`${BASE}/${id}`);
}
/** Placeholder warnings for a subject + body (read-only; nothing is saved). */
export async function checkTemplateFields(categoryCode: number, subject: string, body: string): Promise<TemplateFieldWarning[]> {
  return data<TemplateFieldWarning[]>(await api.post(`${BASE}/check`, { categoryCode, subject, body }, { silentLoading: true } as never));
}
/** Render the template with one job's real values (blank job = the most recently received). */
export async function previewTemplate(categoryCode: number, subject: string, body: string, jobId?: string): Promise<TemplatePreview> {
  return data<TemplatePreview>(await api.post(`${BASE}/preview`, { categoryCode, subject, body, jobId: jobId?.trim() || null }));
}

/** The server's refusal message (400 / 403 / 404 / 409), if any. */
export function templateFieldRefusal(e: unknown): string | undefined {
  return (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message;
}

/** The field as the form edits it. */
export function toFieldInput(f: TemplateField): TemplateFieldInput {
  return {
    categoryCode: f.categoryCode, fieldName: f.fieldName, fieldCode: f.fieldCode, resolverType: f.resolverType,
    sourceTable: f.sourceTable ?? null, sourceColumn: f.sourceColumn ?? null, dataType: f.dataType,
    format: f.format ?? null, displayOrder: f.displayOrder, isActive: f.isActive,
  };
}
